import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { saveRememberedProfile } from '../utils/rememberedProfile';
import { routeForUser } from '../utils/googleAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module-level exchange memo: the route tree can remount (DemoShell loading
// flip, StrictMode), mounting several AuthCallback instances for ONE code.
// Authorisation codes are single-use, so all instances must share a single
// network exchange and its outcome.
const exchangeMemo = {};
function exchangeCodeOnce(code, redirectUri, linkCode) {
  if (!exchangeMemo[code]) {
    exchangeMemo[code] = (async () => {
      const res = await fetch(`${API_URL}/api/auth/google/callback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, redirect_uri: redirectUri, link_code: linkCode }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'OAuth callback failed');
      }
      return res.json();
    })();
  }
  return exchangeMemo[code];
}

/**
 * AuthCallback — handles Google's redirect after the user authorises HiBFF.
 *
 * Mounted at /auth/google (matches the Authorised redirect URIs registered for
 * the OAuth client in Google Cloud Console). Google appends `?code=...` to the
 * URL; we POST that code to our backend, which exchanges it for an ID token,
 * verifies the token, finds-or-creates the user, and returns a HiBFF JWT.
 *
 * Hardening (Sep 2026, QA report C-01/M-08):
 * - Authorisation codes are SINGLE-USE. A reloaded/restored callback tab must
 *   never re-exchange a used code — we track processed codes in sessionStorage
 *   and route already-signed-in users straight to their dashboard instead of
 *   showing a false "Invalid or expired" error.
 * - When the flow started in the native app (state=dl_<code>), the backend
 *   deposits the JWT for the app to collect, and we show a "return to the
 *   app" screen here in the browser.
 * - Failures navigate to /login with tone:'error' so they render in red,
 *   never in the green success box.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser: setAuthUser } = useAuth();
  const hasProcessed = useRef(false);
  const [appHandoff, setAppHandoff] = useState(null); // { route } once a device-link login completes

  // Captured once from the router location (NOT window.location — a late
  // remount during the post-exchange navigation would read the NEW url and
  // wrongly take the "no code" branch).
  const searchRef = useRef(location.search);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processOAuth = async () => {
      try {
        const search = new URLSearchParams(searchRef.current);
        const code = search.get('code');
        const oauthError = search.get('error');
        const state = search.get('state') || '';
        const linkCode = state.startsWith('dl_') ? state.slice(3) : null;

        if (oauthError) {
          navigate('/login', { state: { message: `Google sign-in cancelled: ${oauthError}`, tone: 'error' } });
          return;
        }
        if (!code) {
          navigate('/login', { state: { message: 'Google sign-in failed. Please try again.', tone: 'error' } });
          return;
        }

        // Hard-reload guard (fresh JS context, so the memo is empty but the
        // code was already exchanged in a previous page load of this session):
        // never burn the code twice — recover gracefully instead.
        const codeKey = `hibff_gauth_${code.slice(-24)}`;
        if (sessionStorage.getItem(codeKey) && !exchangeMemo[code]) {
          const existingToken = localStorage.getItem('hibff_auth_token');
          const cached = localStorage.getItem('hibff_user_cache');
          if (existingToken && cached) {
            navigate(routeForUser(JSON.parse(cached)), { replace: true });
          } else {
            navigate('/login', { state: { message: 'That sign-in link was already used. Please tap "Continue with Google" again.', tone: 'error' } });
          }
          return;
        }
        sessionStorage.setItem(codeKey, '1');

        const redirectUri = `${window.location.origin}/auth/google`;
        // Shared, memoized exchange: if another mount already started it,
        // this instance simply awaits the same result.
        const data = await exchangeCodeOnce(code, redirectUri, linkCode);

        localStorage.setItem('hibff_auth_token', data.access_token);
        sessionStorage.removeItem('hibff_auth_token');

        const userData = data.user;
        localStorage.setItem('hibff_user_cache', JSON.stringify(userData));
        if (setAuthUser) setAuthUser(userData);

        // Returning-user welcome hint — Google OAuth is inherently a
        // "remembered" sign-in (token stored persistently above), so we
        // write the hint unconditionally. Stores first name only; see
        // utils/rememberedProfile.js for the full privacy contract.
        // Cleared on logout / session lock / 2-hour idle via AuthContext.
        saveRememberedProfile(userData, true);

        if (linkCode) {
          // Flow started inside the HiBFF app — the app is polling and will
          // sign itself in. Tell the user to head back rather than stranding
          // them in the browser.
          setAppHandoff({ route: routeForUser(userData) });
          return;
        }

        navigate(routeForUser(userData), { replace: true });
      } catch (err) {
        console.error('Google OAuth error:', err);
        navigate('/login', { state: { message: err.message || 'Google sign-in failed.', tone: 'error' } });
      }
    };

    processOAuth();
  }, [navigate, setAuthUser]);

  if (appHandoff) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white px-6" data-testid="auth-callback-app-handoff">
        <div className="text-center max-w-sm">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">✓</span>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">You&apos;re signed in!</h1>
          <p className="text-gray-600 mb-6">
            Head back to the <strong>HiBFF app</strong> — it has signed you in automatically.
          </p>
          <button
            onClick={() => navigate(appHandoff.route, { replace: true })}
            className="text-sm text-purple-600 underline"
            data-testid="auth-callback-continue-browser"
          >
            Or continue in the browser
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white" data-testid="auth-callback-loader">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600 font-medium">Signing you in with Google…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
